import { useState } from 'react'
import { AxiosResponse } from 'axios'
import Modal from 'react-bootstrap/Modal'
import { UseMutateFunction } from 'react-query'
import { useParams } from 'react-router-dom'
import posthog from 'posthog-js'

interface IPayload {
    id: string | undefined
    loanId: number
    note?: string
}
interface IModalProps {
    show: boolean
    onHide: () => void
    type: 'submit'
    mutate: UseMutateFunction<
        AxiosResponse<any, any>,
        unknown,
        IPayload,
        unknown
    >
    loanId: number
    /**
     * A manager REJECTS and may say why; the requester CANCELS their own. The
     * old dialog asked both «آیا وام درخواستی لغو شود؟» with a bare بله/خیر,
     * so a manager's «no» reached the member as a state change with no reason
     * — while the backend had been ready to put one on their timeline.
     */
    asManager?: boolean
}

const DeleteModal = ({
    show,
    onHide,
    mutate,
    loanId,
    asManager = false,
}: IModalProps) => {
    const { id } = useParams()
    const [reason, setReason] = useState('')

    const close = () => {
        setReason('')
        onHide()
    }

    const confirm = () => {
        posthog.capture(
            asManager ? 'loan_demand_rejected' : 'loan_demand_cancelled',
            {
                fund_id: id,
                demand_id: loanId,
                source: 'requests_list',
                // never the text itself
                reason_length: reason.trim().length,
            }
        )
        mutate(asManager ? { id, loanId, note: reason.trim() } : { id, loanId })
        close()
    }

    return (
        <Modal show={show} onHide={close} centered>
            {/* Portalled out of the card, but React still bubbles clicks up
                the component tree — through the card's own «open کارتابل».
                Stopped here so a tap inside the dialog stays in it. */}
            <div className=" p-4" onClick={(event) => event.stopPropagation()}>
                <p className=" text-sm font-bold text-heading">
                    {asManager ? 'درخواست وام رد شود؟' : 'درخواست وام لغو شود؟'}
                </p>
                <p className=" mt-1 text-[11px] leading-6 text-text-secondary">
                    {asManager
                        ? 'عضو در کارتابل درخواستش می‌بیند که رد شده، همراه با دلیلی که اینجا بنویسید.'
                        : 'درخواست از صف مدیران برداشته می‌شود.'}
                </p>
                {asManager && (
                    <>
                        <label
                            htmlFor={`reject-reason-${loanId}`}
                            className=" mt-3 block text-xs font-bold text-heading"
                        >
                            دلیل رد (اختیاری)
                        </label>
                        <textarea
                            id={`reject-reason-${loanId}`}
                            value={reason}
                            onChange={(event) => setReason(event.target.value)}
                            maxLength={1000}
                            rows={3}
                            placeholder="مثلاً: موجودی صندوق فعلاً کافی نیست."
                            className=" mt-2 w-full resize-none rounded-lg border border-stroke bg-canvas p-3 text-xs leading-6 outline-none focus:border-brand"
                        />
                    </>
                )}
                <div className=" mt-4 flex gap-2">
                    <button
                        type="button"
                        onClick={confirm}
                        className=" flex-1 rounded-full bg-danger-on py-2.5 text-sm font-bold text-white"
                    >
                        {asManager ? 'رد درخواست' : 'لغو درخواست'}
                    </button>
                    <button
                        type="button"
                        onClick={close}
                        className=" flex-1 rounded-full border border-stroke bg-white py-2.5 text-sm text-text-body"
                    >
                        انصراف
                    </button>
                </div>
            </div>
        </Modal>
    )
}

export default DeleteModal
